// This is the public-only response from the WordPress companion bridge.
export interface AudiobookRetailer {
  vendor: string;
  label: string;
  url: string;
  logoUrl: string;
}

export interface AudiobookLandingPublic {
  version: 1;
  editionId: number;
  slug: string;
  language: string;
  title: string;
  coverUrl: string;
  retailers: AudiobookRetailer[];
  authorWebsiteUrl: string;
  sourceLandingUrl: string;
  bookishLandingUrl: string;
}

export function safePublicUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') &&
      !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

export function safeRetailers(landing: AudiobookLandingPublic): AudiobookRetailer[] {
  return landing.retailers.filter((retailer) =>
    /^[a-z0-9_-]+$/.test(retailer.vendor) &&
    Boolean(retailer.label.trim()) && safePublicUrl(retailer.url) !== null
  );
}

export function sameBookSlug(left: string, right: string): boolean {
  try { return decodeURIComponent(left) === decodeURIComponent(right); }
  catch { return false; }
}

// Drop every field outside the explicitly public schema, even if the server
// accidentally includes WordPress post meta or other administrative fields.
export function parseAudiobookLanding(value: unknown, requestedSlug: string): AudiobookLandingPublic {
  if (!value || typeof value !== 'object') throw new Error('Invalid audiobook landing response');
  const item = value as Record<string, unknown>;
  if (item.version !== 1 || !Number.isSafeInteger(item.editionId) ||
      typeof item.slug !== 'string' || !sameBookSlug(item.slug, requestedSlug) ||
      typeof item.title !== 'string' || !item.title ||
      typeof item.language !== 'string' ||
      typeof item.coverUrl !== 'string' ||
      typeof item.authorWebsiteUrl !== 'string' ||
      typeof item.sourceLandingUrl !== 'string' ||
      typeof item.bookishLandingUrl !== 'string' ||
      !Array.isArray(item.retailers)) {
    throw new Error('Invalid audiobook landing response');
  }
  const retailers: AudiobookRetailer[] = item.retailers.map((raw) => {
    if (!raw || typeof raw !== 'object') throw new Error('Invalid retailer response');
    const retailer = raw as Record<string, unknown>;
    if (typeof retailer.vendor !== 'string' || typeof retailer.label !== 'string' ||
        typeof retailer.url !== 'string' || typeof retailer.logoUrl !== 'string') {
      throw new Error('Invalid retailer response');
    }
    return { vendor: retailer.vendor, label: retailer.label, url: retailer.url, logoUrl: retailer.logoUrl };
  });
  return {
    version: 1,
    editionId: item.editionId as number,
    slug: item.slug,
    title: item.title,
    language: item.language,
    coverUrl: item.coverUrl,
    retailers,
    authorWebsiteUrl: item.authorWebsiteUrl,
    sourceLandingUrl: item.sourceLandingUrl,
    bookishLandingUrl: item.bookishLandingUrl,
  };
}
